import { useRetainedState } from "@appretio/shared";
import { useCallback, useDeferredValue, useMemo } from "react";
import type { EditConfig } from "../../utils/summary/editPropagation";
import {
  type AggView,
  buildColumnDefs,
  type PlanByMode,
  type SummaryView
} from "../../utils/summary/summaryPageHelpers";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import type { ForecastMetaDataType } from "../useFetchForecastMetaData";

/** Parameters for the summary display controls hook. */
interface UseSummaryDisplayControlsParams {
  /** The page whose controls these are; they are retained per page. */
  page: CubePage;
  /** Metrics sorted by KPI sort order. */
  sortedMetrics: string[];
  /** Aggregation periods that are editable (e.g. PTD, PTG). */
  editableAggs: string[];
  /** Current edit configuration for the grid. */
  editConfig: EditConfig;
  /** Whether index data is loaded. */
  hasIndexData: boolean;
  /** The applied row dimension for column definitions. */
  appliedRowDim: string;
  /** Forecast metadata (used for display labels in column headers). */
  metaData: ForecastMetaDataType | undefined;
}

/**
 * Manages the display controls and the column definitions they build.
 *
 * The category (which KPI columns) and the option toggle have no control of
 * their own: the View picker sets them, and the column menu refines them
 * per metric (``visibility``). The bar keeps plan-by and aggregation.
 */
export function useSummaryDisplayControls({
  page,
  sortedMetrics,
  editableAggs,
  editConfig,
  hasIndexData,
  appliedRowDim,
  metaData
}: UseSummaryDisplayControlsParams) {
  const scope = pageScope(page);
  const [category, setCategory] = useRetainedState<SummaryView | null>(
    scope,
    "category",
    "Compact"
  );
  const [agg, setAgg] = useRetainedState<AggView>(scope, "agg", "ALL");
  const [planBy, setPlanBy] = useRetainedState<PlanByMode>(
    scope,
    "planBy",
    "ABS"
  );
  const [visibility, setVisibility] = useRetainedState<Record<string, boolean>>(
    scope,
    "visibility",
    {}
  );

  const setMetricVisible = useCallback(
    (metric: string, visible: boolean) =>
      setVisibility((prev) => ({ ...prev, [metric]: visible })),
    [setVisibility]
  );

  const deferredCategory = useDeferredValue(category);
  const deferredAgg = useDeferredValue(agg);
  const deferredPlanBy = useDeferredValue(planBy);
  const deferredVisibility = useDeferredValue(visibility);
  const isFilterPending =
    deferredCategory !== category ||
    deferredAgg !== agg ||
    deferredPlanBy !== planBy ||
    deferredVisibility !== visibility;

  const columnDefs = useMemo(
    () =>
      buildColumnDefs({
        metrics: sortedMetrics,
        agg: deferredAgg,
        editableAggs,
        ptdMutable: editConfig.ptdMutable,
        planBy: deferredPlanBy,
        hasIndexData,
        metaData,
        category: deferredCategory,
        rowDim: appliedRowDim,
        visibility: deferredVisibility
      }),
    [
      sortedMetrics,
      deferredAgg,
      editableAggs,
      editConfig.ptdMutable,
      deferredPlanBy,
      hasIndexData,
      metaData,
      deferredCategory,
      appliedRowDim,
      deferredVisibility
    ]
  );

  return {
    category,
    setCategory,
    agg,
    setAgg,
    planBy,
    setPlanBy,
    visibility,
    setVisibility,
    setMetricVisible,
    isFilterPending,
    columnDefs
  };
}
